/**
 * Builds the AI SDK chat transport: auth injection + request-body reshaping to
 * the vercel-backend ChatRequest schema. Generalized from useScoperAiChat:
 * `debug` comes from props (not hardcoded), and `fetch` is injectable so the
 * Scoper debug surface can tee the response body without the engine knowing.
 */

import type { MutableRefObject } from 'react';
import { DefaultChatTransport } from 'ai';
import type { AiChatSettings } from '@repo/ai-contract';

import { authHeaders, CHAT_ENDPOINT, getAccessToken } from './backend';
import { toActiveDraftContext, toBackendMessages } from './draftContext';

import type { AgentContext, AiDraft } from '@/shared/types/chat';

export interface ChatRequestLog {
  url: string;
  body: unknown;
  headers: Record<string, string>;
}

export interface BuildTransportArgs {
  contextRef: MutableRefObject<AgentContext | null>;
  /** Settings snapshot captured at sendMessage time. */
  settingsRef: MutableRefObject<AiChatSettings>;
  activeDraftRef: MutableRefObject<AiDraft | null>;
  debug: boolean;
  fetchImpl?: typeof fetch;
  /** Optional recorder for the Scoper Request tab. */
  onRequest?: (log: ChatRequestLog) => void;
}

/** Drops `undefined` / `'default'` so the backend only sees explicit settings. */
function nonDefaultSettings(settings: AiChatSettings): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(settings).filter(([, value]) => value !== undefined && value !== 'default')
  );
}

export function buildChatTransport(args: BuildTransportArgs) {
  const { contextRef, settingsRef, activeDraftRef, debug, fetchImpl, onRequest } = args;

  return new DefaultChatTransport({
    api: CHAT_ENDPOINT,
    ...(fetchImpl ? { fetch: fetchImpl } : {}),

    prepareSendMessagesRequest: async ({ messages }) => {
      const token = await getAccessToken();
      const ctx = contextRef.current;
      const settings = nonDefaultSettings(settingsRef.current);
      const activeDraftCtx = toActiveDraftContext(activeDraftRef.current);

      const body = {
        messages: toBackendMessages(messages),
        context: ctx ?? { assistantType: 'global' as const },
        ...(activeDraftCtx ? { activeDraft: activeDraftCtx } : {}),
        ...(Object.keys(settings).length > 0 ? { settings } : {}),
        ...(debug ? { debug: true } : {}),
      };

      const headers = authHeaders(token);
      onRequest?.({ url: CHAT_ENDPOINT, body, headers });
      return { body, headers };
    },
  });
}
